import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { TID } from "@/lib/testIds";
import { NavAuth } from "@/components/NavAuth";

const BADGE =
  "https://customer-assets.emergentagent.com/job_aviation-integrity/artifacts/emcpu9jb_generated-image-2026-01-18-16-16-51.1280pix.no%20background.png";

const links = [
  { href: "/#atfq", label: "Podcast" },
  { href: "/voices", label: "Voices" },
  { href: "/calendar", label: "Calendar" },
  { href: "/#investigations", label: "Investigations" },
  { href: "/#about", label: "The Host" },
  { href: "/#survey", label: "Sit Down" },
  { href: "/#contact", label: "Contact" },
];

const MobileMenu = ({ onClose }) => (
  <div className="lg:hidden bg-white border-t border-[var(--line)] px-6 py-6 space-y-4">
    {links.map((l) => (
      <a
        key={l.href}
        href={l.href}
        onClick={onClose}
        className="block text-sm font-semibold uppercase tracking-wider text-[var(--navy)]"
      >
        {l.label}
      </a>
    ))}
    <a
      href="/#atfq"
      onClick={onClose}
      className="block bg-[var(--navy)] text-white px-5 py-3 text-xs font-bold uppercase tracking-widest text-center mt-4"
    >
      Listen · ATFQ
    </a>
    <div className="pt-2">
      <NavAuth textCls="text-[var(--navy)]" hoverCls="hover:text-[var(--accent)]" />
    </div>
  </div>
);

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const textCls = "text-[var(--navy)]";
  const hoverCls = "hover:text-[var(--accent)]";
  const brandSlash = "text-[var(--accent)]";

  return (
    <header
      className={`fixed top-9 md:top-10 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur border-b border-[var(--line)]"
          : "bg-transparent"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-20 h-24 md:h-28 flex items-center justify-between">
        <a
          href="/#top"
          data-testid={TID.navBrand}
          className="flex items-center gap-3 group"
        >
          <img
            src={BADGE}
            alt="ATFQ · Answer The F**king Question — Vic Appiah's watchdog podcast badge"
            className="h-20 md:h-24 w-auto drop-shadow-md"
          />
          <span className={`font-display text-lg md:text-xl tracking-tight ${textCls} hidden sm:inline`}>
            VICTOR <span className={brandSlash}>APPIAH</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className={`text-sm font-medium ${textCls} ${hoverCls} transition-colors uppercase tracking-wider`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <a
            href="/#atfq-ask"
            data-testid={TID.navPetition}
            className={`text-xs font-bold uppercase tracking-widest ${textCls} ${hoverCls} transition-colors`}
          >
            Ask A Question
          </a>
          <a
            href="/#atfq"
            data-testid={TID.navJoin}
            className={`px-5 py-3 text-xs font-bold uppercase tracking-widest transition-colors ${
              scrolled
                ? "bg-[var(--navy)] text-white hover:bg-[var(--navy-soft)]"
                : "bg-[var(--accent)] text-[var(--navy-deep)] hover:bg-[#5fa1ef]"
            }`}
          >
            Listen · ATFQ
          </a>
          <NavAuth textCls={textCls} hoverCls={hoverCls} />
        </div>

        <button
          className={`lg:hidden p-2 ${textCls}`}
          data-testid={TID.navMobileToggle}
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && <MobileMenu onClose={() => setOpen(false)} />}
    </header>
  );
};

export default Nav;
